import React, { FC, useEffect, useState } from "react";
import Icon from "components/Elements/Icon/Icon.tsx";
import UploadFilesPreview from "components/Pages/Home/UploaderComponent/UploadFilesPreview.js";
import UploaderEmptyDragArea from "components/Pages/Home/UploaderComponent/UploaderDropArea.js";
import UploaderHeader from "components/Pages/Home/UploaderComponent/UploaderHeader.js";
import UploadProgressComponent from "components/Pages/Home/Reusable/UploadProgressComponent.js";
import ReuploadSideBar from "./ReuploadSideBar.tsx";
import { getAllowedVideoDuration } from "components/Pages/Home/Helpers/uploaderAPI.js";
import { useDragUploader } from "components/Pages/Home/Helpers/useDragUploader.js";
import { useUploaderLogic } from "components/Pages/Home/Helpers/useUploaderLogic.js";
import { UploaderDragArea } from "components/Pages/Home/UploaderComponent/UploaderComponent.js";
import { useContent } from "contexts/contentContext/contentContext";
import { useUploader } from "contexts/uploaderContext/uploaderContext.js";
import { UploaderContext } from "components/Pages/Home/UploaderComponent/HomeUploader.js";
import { useMail } from "contexts/mailContext/mailContext";
import { useUser } from "contexts/userContext/userContext.js";
import { ButtonAccessability } from "components/Elements/Button/Button";
import { useToasts } from "react-toast-notifications";

import "../../Pages/Home/UploaderComponent/uploader.scss";
import { UploaderFooter } from "components/Pages/Home/UploaderComponent/UploaderFooter.js";

type handleFinishUploadProps = {
  newCrfToken: string;
  reuploadingMailId: string;
  uploadedFiles: {
    size: number;
  }[];
};

export type ReuploadProps = {
  handleFinishUpload: (props: handleFinishUploadProps) => void;
};

// handleFinishUpload func is different for all reupload places
// it takes care about external calls to get uploaded files, new contributors data, files counting etc

const ReuploadComponent: FC<ReuploadProps> = ({
  handleFinishUpload = () => {},
}) => {
  const { getTextByKey } = useContent();
  const { addToast } = useToasts();
  const { setReuploadingMail, getReuploadingMail } = useMail();
  const [minimized, setMinimized] = useState(false);
  const { uploaderGetFiles, getUploading, setAllowedVideoDuration } =
    useUploader();
  const { getUserData } = useUser();

  const handleFinishUploadWithToast = (
    exitCode: string,
    newCrfToken: string
  ) => {
    handleFinishUpload({
      newCrfToken,
      reuploadingMailId: getReuploadingMail(),
      uploadedFiles: uploaderGetFiles(),
    });
    setReuploadingMail(null);

    addToast(
      getTextByKey("reupload-success").replace(
        "#filesCount#",
        uploaderGetFiles().length
      ),
      {
        appearance: "success",
      }
    );
  };

  // uploader logic
  const { filesCount, filesProgress } = useUploaderLogic({
    reuploadEmail: getUserData().UserName,
    setFinishUpload: handleFinishUploadWithToast,
  });

  const { handleDrop, handleDrag, dragged } = useDragUploader();

  const [listView, setListView] = useState(false);

  useEffect(() => {
    getAllowedVideoDuration(setAllowedVideoDuration);
  }, []);

  // of minimize need to change class of container
  useEffect(() => {
    if (minimized) {
      document.getElementById("reupload-component")?.classList.add("minimized");
    } else {
      document
        .getElementById("reupload-component")
        ?.classList.remove("minimized");
    }
  }, [minimized]);

  // add event listener to close component on click outside
  useEffect(() => {
    document
      .getElementById("reupload-component")
      ?.addEventListener("click", handleClick);
    return () => {
      document
        .getElementById("reupload-component")
        ?.removeEventListener("click", handleClick);
    };
  }, []);

  const handleClick = (e: Event) => {
    if (e.target === e.currentTarget) {
      if (getUploading()) {
        // minimize logic
      } else {
        setReuploadingMail(null);
      }
    }
  };

  const handleMinimize = () => {
    if (getUploading()) {
      setMinimized(true);
    } else {
      setReuploadingMail(null);
    }
  };

  const handleMaximize = (e: Event) => {
    e.stopPropagation();
    setMinimized(false);
  };

  return minimized ? (
    <div className="minimized-reupload-container" onClick={handleMinimize}>
      <ButtonAccessability buttonClassName="maximize" onClick={handleMaximize}>
        <Icon name="expand" blue />
      </ButtonAccessability>
      <UploaderContext.Provider
        value={{ uploadCounter: filesCount, filesProgress }}
      >
        <UploadProgressComponent />
      </UploaderContext.Provider>
    </div>
  ) : (
    <div
      className="home-upload-container"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div className="window-controls">
        <ButtonAccessability
          onClick={handleMinimize}
          buttonClassName="minimize"
          title={getTextByKey("minimize")}
        >
          <span>__</span>
        </ButtonAccessability>
        {getUploading() ? null : (
          <ButtonAccessability
            onClick={() => {
              setReuploadingMail(null);
            }}
            title={getTextByKey("close")}
          >
            <Icon name="x" gray />
          </ButtonAccessability>
        )}
      </div>

      <UploaderContext.Provider
        value={{ uploadCounter: filesCount, filesProgress }}
      >
        <div className="home-upload-content">
          <UploaderHeader {...{ listView, setListView }} />
          {uploaderGetFiles().length !== 0 ? (
            <div
              className="home-upload-content-workspace"
              onDrop={handleDrop}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
            >
              {dragged && !getUploading() ? (
                <div className="drag-zone-highlight">
                  <Icon name="upload-home" blue />
                  <div className="drop-here-text">
                    {getTextByKey("drop-here-text")}
                  </div>
                </div>
              ) : null}
              <UploadFilesPreview {...{ listView }} />
              <div className="flex-spacer"></div>
              <UploaderFooter />
            </div>
          ) : (
            <UploaderEmptyDragArea {...{ handleDrop, handleDrag, dragged }} />
          )}
        </div>
        <ReuploadSideBar />
      </UploaderContext.Provider>
    </div>
  );
};

export default ReuploadComponent;
